/* eslint-disable jsx-control-statements/jsx-use-if-tag */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedNumber } from 'react-intl';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { rgba } from 'polished';
import { withSize } from 'react-sizeme';

import useForm from '../hooks/useForm';
import Sizes from '../definitions/Sizes';
import Colours from '../definitions/Colours';
import Typography from '../definitions/Typography';
import MessagePropType from '../definitions/MessagePropType';
import Paragraph from './Paragraph';
import TooltipAnchor from './TooltipAnchor';
import {
    MIDDLE,
    BOTTOM,
    END,
} from './Tooltip';


function ComparisonTable({
    headings,
    headingsClassNames,
    lines,
    linesClassNames,
    size,
    noResultLine,
    variant,
    ...otherProps
}) {
    const form = useForm();
    const isNarrow = size.width < 380;

    return (
        <ComparisonTable.Container
            isNarrow={isNarrow}
            {...otherProps}
        >
            <div className="heading row">
                <div className="headerLabel">
                    <If condition={headings?.label}>
                        <Paragraph
                            className="column"
                            message={headings.label}
                        />
                    </If>
                </div>

                <div className="columns">
                    <Paragraph
                        className={classNames('column', headingsClassNames, {
                            [`${VARIANTS.UNIFORM} rh-title-xs rh-fg-blueberry-dark`]: variant === VARIANTS.UNIFORM,
                        })}
                        message={headings.columnTwo}
                    />

                    <Paragraph
                        className={classNames('column rh-fg-blueberry-dark', headingsClassNames, {
                            [`${VARIANTS.UNIFORM} rh-title-xs`]: variant === VARIANTS.UNIFORM,
                        })}
                        message={headings.columnThree}
                    />
                </div>
            </div>

            <For
                each="line"
                of={lines}
                index="i"
            >
                <div
                    className={classNames(
                        'content',
                        'row',
                        { result: (i == lines.length - 1) && !noResultLine },
                    )}
                    key={`summaryLine-${i}`}
                >
                    <div className="contentLabel">
                        <Paragraph message={line.label} />

                        <If condition={line.tooltip}>
                            <TooltipAnchor
                                className="tooltip"
                                message={line.tooltip}
                                // Location should be revisted when glamazon update
                                // for better tooltip handling on small displays
                                tooltipLocation={BOTTOM}
                                nubbinAlignment={isNarrow ? END : MIDDLE}
                                width={isNarrow ? '16rem' : '25rem'}
                            />
                        </If>
                    </div>


                    <div className="columns">
                        <Paragraph
                            data-test-name={`comparison-table-line-${i}-column-1-value`}
                            className={classNames('column', linesClassNames, {
                                [`${VARIANTS.UNIFORM} rh-fg-blackberry`]: variant === VARIANTS.UNIFORM,
                            })}
                            message={isColumnValueFound(form, line.columnOne.value ?? line.columnOne)
                                ? (
                                    <FormattedNumber
                                        value={getColumnValue(form, line.columnOne.value ?? line.columnOne)}
                                        format={line.columnOne.format ?? line.format}
                                        unit={line.unit}
                                        {...(line.style ? { style: line.style } : {})}
                                    />
                                )
                                : '-'
                            }
                        />

                        <Paragraph
                            data-test-name={`comparison-table-line-${i}-column-2-value`}
                            className={classNames('column', linesClassNames, {
                                [`${VARIANTS.DEFAULT} rh-fg-blueberry-dark`]: variant === VARIANTS.DEFAULT,
                                [`${VARIANTS.UNIFORM} rh-fg-blackberry`]: variant === VARIANTS.UNIFORM,
                            })}
                            message={isColumnValueFound(form, line.columnTwo.value ?? line.columnTwo)
                                ? (
                                    <FormattedNumber
                                        value={getColumnValue(form, line.columnTwo.value ?? line.columnTwo)}
                                        format={line.columnTwo.format ?? line.format}
                                        unit={line.unit}
                                        {...(line.style ? { style: line.style } : {})}
                                    />
                                )
                                : '-'
                            }
                        />
                    </div>
                </div>
            </For>
        </ComparisonTable.Container>
    );
}

function getColumnValue(form, value) {
    return typeof value === 'string'
        ? form.getValue(value)
        : typeof value === 'function'
            ? value(form.values)
            : value; // Just pass through the number
}

function isColumnValueFound(form, value) {
    return getColumnValue(form, value) != null;
}


const ColumnSingleValueProp = PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.func,
]);

const ColumnObjectProps = PropTypes.shape({
    value: ColumnSingleValueProp.isRequired,
    format: PropTypes.string,  // format for this specific value
});

const VARIANTS = {
    DEFAULT: 'default',
    UNIFORM: 'uniform',
};

ComparisonTable.propTypes = {
    headings: PropTypes.shape({
        label: MessagePropType,
        columnTwo: MessagePropType.isRequired,
        columnThree: MessagePropType.isRequired,
    }).isRequired,
    headingsClassNames: PropTypes.string,
    lines: PropTypes.arrayOf(PropTypes.shape({
        label: MessagePropType.isRequired,
        columnOne: PropTypes.oneOfType([
            ColumnObjectProps,
            ColumnSingleValueProp,
        ]).isRequired,
        columnTwo: PropTypes.oneOfType([
            ColumnObjectProps,
            ColumnSingleValueProp,
        ]).isRequired,
        format: PropTypes.string, // format for all values in row/line
        tooltip: MessagePropType,
    })).isRequired,
    linesClassNames: PropTypes.string,
    noResultLine: PropTypes.bool,
    size: PropTypes.object.isRequired,
    variant: PropTypes.oneOf([
        VARIANTS.DEFAULT,
        VARIANTS.UNIFORM,
    ]),
};

ComparisonTable.defaultProps = {
    headingsClassNames: undefined,
    linesClassNames: undefined,
    noResultLine: false,
    variant: VARIANTS.DEFAULT,
};

ComparisonTable.Container = styled.section`
    border-radius: 6px;
    border: 1px solid ${Colours.BLUEBERRY_DARK};

    > .row {
        display: flex;
        flex-wrap: wrap;

        p {
            margin: 0;
            padding: ${Sizes.SPACING.ONE};

            flex-grow: ${p => p.isNarrow ? 0 : 1};
        }

        .headerLabel {
            flex-basis: 0;
            flex-grow: 1;

            min-width: 15rem;
        }

        > .contentLabel {
            display: flex;
            align-items: center;
            justify-content: ${p => p.isNarrow ? 'center' : 'initial'};
            text-align: ${p => p.isNarrow ? 'center' : 'initial'};

            flex-basis: 0;
            flex-grow: 1;

            min-width: 15rem;

            > .tooltip {
                margin-right: ${Sizes.SPACING.ONE};
            }
        }

        .columns {
            display: flex;
            justify-content: center;

            flex-shrink: 1;
            flex-grow: 1;
            flex-basis: 0;

            min-width: 15rem;

            .column {
                text-align: center;
                flex: 1;
            }
        }
    }

    > .heading {
        border-bottom: 1px solid ${Colours.BLUEBERRY_DARK};

        .columns {
            align-items: center;
        }
    }

    > .content {
        .contentLabel {
            background-color: ${rgba(Colours.BLUEBERRY_LIGHTEST, 0.5)};
        }
    }

    > .result {
        border-top: 1px solid ${Colours.BLUEBERRY_DARK};

        .contentLabel {
            background-color: ${Colours.BLUEBERRY_LIGHTEST};

            p {
                color: ${Colours.BLUEBERRY_DARK};
                font-weight: ${Typography.WEIGHTS.MEDIUM};
            }
        }

        .column {
            font-weight: ${Typography.WEIGHTS.MEDIUM};
        }
    }
`;

export default withSize()(observer(ComparisonTable));
